/**
 * Vee-validate v2 compatibility shim for Vue 3.
 *
 * Provides a `v-validate` directive and injects an `errors` bag mixin
 * so existing templates (v-validate, errors.has(), errors.first()) work
 * without any template changes.
 *
 * Supports rules: required, email, min, max, numeric, confirmed, alpha_spaces,
 * alpha_num, regex, url, between, decimal, integer.
 */
import { reactive } from "vue";

// ─── Built-in rule validators ──────────────────────────────────────────────
const RULES = {
    required(value) {
        if (value === null || value === undefined || value === false)
            return "This field is required";
        if (typeof value === "string" && value.trim() === "")
            return "This field is required";
        if (Array.isArray(value) && value.length === 0)
            return "This field is required";
        return true;
    },
    email(value) {
        return (
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
            "Please enter a valid email address"
        );
    },
    min(value, [length]) {
        return (
            String(value).length >= parseInt(length) ||
            `Must be at least ${length} characters`
        );
    },
    max(value, [length]) {
        return (
            String(value).length <= parseInt(length) ||
            `Must be at most ${length} characters`
        );
    },
    numeric(value) {
        return /^\d+$/.test(value) || "This field must be a number";
    },
    integer(value) {
        return (
            Number.isInteger(Number(value)) || "This field must be an integer"
        );
    },
    decimal(value, [decimals = 2]) {
        const pattern = new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`);
        return (
            pattern.test(String(value)) ||
            `This field must be a decimal with up to ${decimals} decimal places`
        );
    },
    between(value, [min, max]) {
        const n = parseFloat(value);
        return (
            (n >= parseFloat(min) && n <= parseFloat(max)) ||
            `Must be between ${min} and ${max}`
        );
    },
    alpha_spaces(value) {
        return (
            /^[a-zA-Z\s]*$/.test(value) ||
            "This field may only contain alphabetic characters and spaces"
        );
    },
    alpha_num(value) {
        return (
            /^[a-zA-Z0-9]*$/.test(value) ||
            "This field may only contain alphanumeric characters"
        );
    },
    url(value) {
        try {
            new URL(value);
            return true;
        } catch {
            return "This field must be a valid URL";
        }
    },
    regex(value, [pattern]) {
        return (
            new RegExp(pattern).test(value) || "This field format is invalid"
        );
    },
    confirmed(value, [targetRef], el) {
        const form = el ? el.closest("form") : null;
        const target = form
            ? form.querySelector(`[name="${targetRef}"]`)
            : null;
        const targetValue = target ? target.value : targetRef;
        return (
            value === targetValue ||
            "Password and Confirm Password does not match"
        );
    }
};

// ─── ErrorBag (mimics vee-validate v2 ErrorBag) ───────────────────────────
function createErrorBag(vmId) {
    const bag = reactive({
        vmId,
        items: [],
        get itemslength() {
            return bag.items.length;
        },
        set itemslength(v) {
            /* noop */
        },

        has(fieldKey) {
            return bag.items.some((i) => i.field === fieldKey);
        },
        first(fieldKey) {
            const item = bag.items.find((i) => i.field === fieldKey);
            return item ? item.msg : undefined;
        },
        clear(scope) {
            if (scope) {
                bag.items = bag.items.filter(
                    (i) => !i.field.startsWith(scope + ".")
                );
            } else {
                bag.items = [];
            }
        },
        remove(fieldKey) {
            bag.items = bag.items.filter((i) => i.field !== fieldKey);
        },
        add(fieldKey, msg) {
            bag.remove(fieldKey);
            bag.items.push({ field: fieldKey, msg, vmId });
        },
        collect(fieldKey) {
            return bag.items
                .filter((i) => i.field === fieldKey)
                .map((i) => i.msg);
        },
        count() {
            return bag.items.length;
        },
        all() {
            return bag.items;
        },
        any() {
            return bag.items.length > 0;
        }
    });
    return bag;
}

// ─── Validation logic ─────────────────────────────────────────────────────

/**
 * Normalize v-validate binding to a pipe-separated rule string.
 * Handles both string form ("required|email") and object form ({required: true, min: 3}).
 */
function normalizeRules(rulesRaw) {
    if (!rulesRaw) return "";
    if (typeof rulesRaw === "string") return rulesRaw;
    if (typeof rulesRaw === "object") {
        return Object.entries(rulesRaw)
            .filter(
                ([, enabled]) =>
                    enabled !== false &&
                    enabled !== null &&
                    enabled !== undefined
            )
            .map(([rule, param]) =>
                param === true ? rule : `${rule}:${param}`
            )
            .join("|");
    }
    return String(rulesRaw);
}

function runRules(value, rulesRaw, el) {
    const rulesString = normalizeRules(rulesRaw);
    if (!rulesString) return null;
    const rules = rulesString.split("|");
    for (const ruleStr of rules) {
        const [ruleName, paramsStr] = ruleStr.split(":");
        const trimmedName = ruleName.trim();
        const params = paramsStr ? paramsStr.split(",") : [];
        const rule = RULES[trimmedName];
        if (!rule) continue;
        const result = rule(value, params, el);
        if (result !== true) return String(result);
    }
    return null;
}

function getFieldKey(el) {
    // data-vv-name takes priority (explicitly set for v-select and similar components)
    const name =
        el.getAttribute("data-vv-name") ||
        el.getAttribute("name") ||
        el.id ||
        "";
    const scope = el.closest("[data-vv-scope]")?.getAttribute("data-vv-scope");
    return scope ? `${scope}.${name}` : name;
}

/**
 * Read the current value for a form element.
 *
 * For component-based controls (v-select, etc.) el is the root <div> and
 * el.value is always undefined. We use several fallbacks in order:
 *   1. _veeGetValue getter set by the directive (reads Vue props)
 *   2. DOM inspection: if a .vs__selected child exists the field is non-empty
 *   3. Native el.value
 */
function getElementValue(el) {
    if (typeof el._veeGetValue === "function") {
        const val = el._veeGetValue();
        if (val !== undefined && val !== null) return val;
    }
    // DOM-based fallback for vue-select: .vs__selected exists when something is chosen
    if (typeof el.querySelector === "function") {
        const vsSelected = el.querySelector(".vs__selected");
        if (vsSelected) {
            return vsSelected.textContent?.trim() || "__selected__";
        }
        // No .vs__selected → nothing selected in v-select → treat as empty
        if (
            el.classList?.contains("vs__dropdown-container") ||
            el.querySelector(".vs__dropdown-toggle")
        ) {
            return undefined;
        }
    }
    return el.value;
}

function validateElement(el, rulesRaw, instance) {
    if (!instance || typeof instance.errors?.has !== "function") return;
    const value = getElementValue(el);
    const fieldKey = getFieldKey(el);
    const error = runRules(value, rulesRaw, el);
    if (error) {
        instance.errors.add(fieldKey, error);
    } else {
        instance.errors.remove(fieldKey);
    }
}

// ─── Directive ────────────────────────────────────────────────────────────
export const vValidate = {
    mounted(el, binding, vnode) {
        el._veeRules = binding.value || "";
        // `binding.instance` is the component where `v-validate` was written in the
        // template (e.g. MyAddresses). `vnode.ctx` is NOT reliable for this: when
        // v-validate is put on a component (e.g. v-select), Vue forwards the
        // directive to that component's own root element vnode, so `vnode.ctx`
        // resolves to v-select's own internal instance — which also has an `errors`
        // bag (the mixin applies to every component) but it's the WRONG bag, so
        // errors added/removed there are invisible to the page's own errors.has().
        el._veeInstance = binding.instance;

        if (vnode.component) {
            // v-validate on a Vue component (e.g. v-select): el is the root <div>,
            // el.value is always undefined. Read modelValue from component props instead.
            el._veeGetValue = () =>
                vnode.component?.props?.modelValue ??
                vnode.component?.ctx?.modelValue;
        }

        el._veeOnInput = () =>
            validateElement(el, el._veeRules, el._veeInstance);
        el._veeOnBlur = () =>
            validateElement(el, el._veeRules, el._veeInstance);
        el.addEventListener("input", el._veeOnInput);
        el.addEventListener("blur", el._veeOnBlur);

        // Component-based controls (v-select, etc.) don't emit native DOM events
        // when their value changes via selection, so the listeners above never fire
        // for them — the error would otherwise only clear on the next validateAll()
        // at submit. Watch the DOM directly instead: vue-select toggles
        // `.vs__selected` in/out of the tree on selection.
        el._veeObserver = new MutationObserver(() => {
            validateElement(el, el._veeRules, el._veeInstance);
        });
        el._veeObserver.observe(el, {
            childList: true,
            subtree: true,
            characterData: true
        });
    },
    updated(el, binding, vnode) {
        el._veeRules = binding.value || "";
        el._veeInstance = binding.instance;
        if (vnode.component) {
            el._veeGetValue = () =>
                vnode.component?.props?.modelValue ??
                vnode.component?.ctx?.modelValue;
        }
    },
    beforeUnmount(el) {
        el.removeEventListener("input", el._veeOnInput);
        el.removeEventListener("blur", el._veeOnBlur);
        if (el._veeObserver) el._veeObserver.disconnect();
    }
};

// ─── $validator per-component object (mimics vee-validate v2 this.$validator) ─
function createValidator(self) {
    return {
        extend(name, rule) {
            RULES[name] = (value, params, el) => {
                const r = rule.validate(value, params, el);
                if (r && typeof r.then === "function") {
                    return r.then((res) => {
                        if (res === true) return true;
                        const valid =
                            typeof res === "object" ? res.valid : !!res;
                        return valid
                            ? true
                            : rule.getMessage(
                                  "field",
                                  params,
                                  (res && res.data) || {}
                              );
                    });
                }
                return r === true ? true : rule.getMessage("field", params, {});
            };
        },

        validateAll(scope) {
            return self.$nextTick().then(() => {
                const root = self.$el;
                if (!root) return true;
                // In Vue 3, $el can be a text/comment node when using fragments.
                // querySelectorAll only exists on Element nodes, so fall back to parentElement.
                const rootEl =
                    root instanceof Element
                        ? root
                        : root.parentElement || document.body;
                const allEls = Array.from(rootEl.querySelectorAll("*")).filter(
                    (el) => el._veeRules
                );
                const targets = scope
                    ? allEls.filter((el) =>
                          getFieldKey(el).startsWith(scope + ".")
                      )
                    : allEls;

                const results = targets.map((el) => {
                    try {
                        const fieldKey = getFieldKey(el);
                        const value = getElementValue(el);
                        const err = runRules(value, el._veeRules, el);
                        if (err) {
                            self.errors.add(fieldKey, err);
                            return false;
                        } else {
                            self.errors.remove(fieldKey);
                            return true;
                        }
                    } catch (_) {
                        return true; // skip elements we can't validate
                    }
                });
                return results.every(Boolean);
            });
        },

        validate(_field, _value) {
            return Promise.resolve(true);
        },

        reset() {
            self.errors.clear();
        },

        localize() {
            /* vee-validate v2 locale — no-op */
        },

        get errors() {
            return self.errors;
        },
        get fields() {
            return {};
        }
    };
}

// ─── Global mixin (injects this.errors + this.$validator into every component) ─
let _uidCounter = 0;
export const validationMixin = {
    beforeCreate() {
        _uidCounter++;
        this._veeUid = _uidCounter;
        this.errors = createErrorBag(this._veeUid);
    },
    created() {
        this.$validator = createValidator(this);
    }
};

// ─── Plugin ───────────────────────────────────────────────────────────────
export default {
    install(app) {
        app.directive("validate", vValidate);
        app.mixin(validationMixin);
    }
};
